import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../shared/services/api.service';
import { ChartDataService } from '../../services/chart-data.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject } from 'rxjs';
import { QueryHistoryService } from '../../../market-fan-exploration/services/query-history.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import {
  ChartMetadata,
  GetChartQuery,
  GetChartQueryVariables,
  GetQueryResultQuery,
  GetQueryResultQueryVariables,
  GetQueryWithExecutionRecordsQuery,
  GetQueryWithExecutionRecordsQueryVariables,
  QueryResultData,
  QueryResultInput,
} from '../../../../API';
import { GetChart, GetQueryResult, GetQueryWithExecutionRecords } from '../../../../graphql/queries';

@Component({
  selector: 'app-build-chart',
  templateUrl: './build-chart.component.html',
  styleUrl: './build-chart.component.scss',
})
export class BuildChartComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  stepper = 0;
  isLoadingChart = false;
  orgId = '';
  private chartIdProcessed = false;

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private chartDataService: ChartDataService,
    private nzMessageService: NzMessageService,
    private queryHistory: QueryHistoryService,
    private userManagementService: UserManagementService,
    @Inject(NZ_MODAL_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.orgId = this.userManagementService.getOrgId();
    if (this.orgId) {
      this.processChartId();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.chartDataService.chartMetadata = null;
    this.chartDataService.setData([]);
    this.chartDataService.setType('{}');
  }

  private processChartId(): void {
    const chartId = this.data?.chartId;
    if (chartId) {
      this.chartIdProcessed = true;
      this.stepper = 1;
      this.loadChartForEdit(chartId);
    }
  }

  private loadChartForEdit(chartId: string): void {
    this.isLoadingChart = true;
    this.apiService
      .query<GetChartQuery, GetChartQueryVariables>(GetChart, {
        chartId: chartId,
        orgId: this.orgId,
      })
      .then((response) => {
        const chartData = response.data?.GetChart as ChartMetadata;
        this.chartDataService.chartMetadata = chartData;
        return Promise.all([this.getQueryData(chartData), this.getAggregateResults(chartData)]);
      })
      .then(([queryResult, aggregateResult]) => {
        const queryRecord = queryResult.data?.GetQueryWithExecutionRecords;
        if (queryRecord?.metadata) {
          this.chartDataService.setType(queryRecord.metadata);
        }
        const resultData = aggregateResult.data?.GetQueryResult;
        const results = resultData?.data as QueryResultData;
        if (results?.rows) {
          const parsedResultData = JSON.parse(results.rows);
          this.chartDataService.setData(parsedResultData);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load chart for edit ${error}`);
      })
      .finally(() => (this.isLoadingChart = false));
  }

  getQueryData(chartData: ChartMetadata) {
    return this.apiService.query<GetQueryWithExecutionRecordsQuery, GetQueryWithExecutionRecordsQueryVariables>(
      GetQueryWithExecutionRecords,
      {
        queryId: chartData.queryId!,
        orgId: this.orgId,
        executionId: chartData.executionId!,
      },
    );
  }

  getAggregateResults(chartData: ChartMetadata) {
    const { queryId, executionId, athenaExecutionId } = chartData;
    const input: QueryResultInput = {
      orgId: this.orgId,
      queryId: queryId ?? '',
      executionId: executionId ?? '',
      athenaExecutionId: athenaExecutionId ?? '',
    };

    return this.apiService.query<GetQueryResultQuery, GetQueryResultQueryVariables>(GetQueryResult, {
      input,
    });
  }

  goToNextStep(): void {
    // TODO: Add Validation for next step
    this.stepper = 1;
  }

  back(): void {
    this.stepper = 0;
  }
}
